import { useEffect } from "react"
import { router, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/Button"
import { settings_task_settings_manual_trigger_quarterly_path, settings_task_settings_path } from "@/lib/routes"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import { NumberField } from "@/pages/Settings/components/PolicyFields"
import PeopleCircles from "@/components/PeopleCircles"
import ReviewerFields from "@/pages/Settings/components/ReviewerFields"
import SettingsSection from "@/pages/Settings/components/SettingsSection"
import type { Person } from "@/lib/person"
import type { QuarterlyRevisionSetting } from "@/pages/Settings/TaskSettings"

// Filled once someone is in charge. Days always hold a number and reviewers
// are optional, so neither counts.
function filled(m: QuarterlyRevisionSetting) {
  return m.recipient_user_ids.length > 0
}

// One config row per market. Quarters are calendar quarters (Jan/Apr/Jul/Oct)
// for every market, so there are no dates to pick — only how far ahead the
// task appears, the PICs and the reviewers.
function MarketRow({ market, onChange, people, readOnly, disabled }: {
  market: QuarterlyRevisionSetting
  onChange: (patch: Partial<QuarterlyRevisionSetting>) => void
  people: Person[]
  readOnly: boolean
  disabled: boolean
}) {
  return (
    <div className="flex flex-col gap-3.5">
      <p className="flex items-center gap-2 text-sm text-foreground">
        <span className="w-40 shrink-0">Task creation</span>
        <NumberField
          value={market.days_before}
          onChange={(n) => onChange({ days_before: n })}
          min={0}
          disabled={disabled}
          label={`${market.name} days before quarter start`}
        />{" "}
        days before each quarter starts
      </p>

      <div className="flex items-start gap-2">
        <span className="w-40 shrink-0 pt-1.5 text-sm text-foreground">People in Charge</span>
        <PeopleCircles
          title="People in Charge"
          people={people}
          selectedIds={market.recipient_user_ids}
          onChange={(ids) => onChange({ recipient_user_ids: ids })}
          editable={!readOnly}
          disabled={disabled}
        />
      </div>

      <ReviewerFields
        skipReview={market.skip_review}
        reviewerIds={market.reviewer_user_ids}
        people={people}
        readOnly={readOnly}
        disabled={disabled}
        onChange={(patch) => onChange(patch)}
      />
    </div>
  )
}

// Its own form: Save sends only the `quarterly_revisions` rows.
export default function QuarterlyRevisionCard({ id, markets, people, readOnly, onUnfilledChange }: {
  id: string
  markets: QuarterlyRevisionSetting[]
  people: Person[]
  readOnly: boolean
  // Reports whether any market is still unfilled, from the live form values.
  onUnfilledChange?: (id: string, unfilled: boolean) => void
}) {
  const { data, setData, patch, processing } = useForm<{ quarterly_revisions: QuarterlyRevisionSetting[] }>({
    quarterly_revisions: markets,
  })
  const disabled = readOnly || processing

  const unfilled = data.quarterly_revisions.some((m) => !filled(m))
  useEffect(() => { onUnfilledChange?.(id, unfilled) }, [id, unfilled, onUnfilledChange])

  const updateMarket = (org_node_id: string, patchRow: Partial<QuarterlyRevisionSetting>) => {
    setData("quarterly_revisions", data.quarterly_revisions.map((m) =>
      (m.org_node_id === org_node_id ? { ...m, ...patchRow } : m),
    ))
  }

  return (
    <SettingsSection
      id={id}
      title="Top Down Quarterly Revision"
      description="Before each quarter, the People in Charge get a task to revise the published plan with the actuals so far."
      action={!readOnly && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => router.post(settings_task_settings_manual_trigger_quarterly_path(), {}, { preserveScroll: true })}
        >
          Manual Trigger
        </Button>
      )}
      canSave={!readOnly}
      processing={processing}
      onSave={() => patch(settings_task_settings_path(), { preserveScroll: true })}
    >
      {data.quarterly_revisions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No markets yet — add country nodes in Org Structure first.</p>
      ) : (
        <Tabs defaultValue={data.quarterly_revisions[0].org_node_id} className="flex flex-col gap-5">
          <TabsList>
            {data.quarterly_revisions.map((market) => (
              <TabsTrigger key={market.org_node_id} value={market.org_node_id} dot={!filled(market)}>{market.name}</TabsTrigger>
            ))}
          </TabsList>
          {data.quarterly_revisions.map((market) => (
            <TabsContent key={market.org_node_id} value={market.org_node_id}>
              <MarketRow
                market={market}
                onChange={(patchRow) => updateMarket(market.org_node_id, patchRow)}
                people={people}
                readOnly={readOnly}
                disabled={disabled}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </SettingsSection>
  )
}
